import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type Lang = 'tr' | 'en';

type Dict = Record<string, string>;

type I18nContextType = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string) => string;
};

const I18nContext = createContext<I18nContextType | null>(null);

const DICTS: Record<Lang, Dict> = {
  tr: {
    'nav.home': 'Anasayfa',
    'nav.cars': 'Araç Filomuz',
    'nav.about': 'Hakkımızda',
    'nav.corporate': 'Kurumsal Kiralama',
    'nav.callNow': 'Hemen Ara',
    'nav.individual': 'Bireysel Kiralama',

    'hero.title': 'Hızlı ve Güvenilir Araç Kiralama',
    'hero.subtitle': 'En uygun fiyatlarla araç kiralayın',
    'hero.cta': 'Hemen Teklif Alın',

    'campaign.title': 'Kampanya',
    'campaign.line1': "Bu ay web'e özel %5 indirim! Kupon:",
    'campaign.line2': 'Uzun dönem kiralamada özel fiyatlar için teklif alın.',
    'campaign.line3': '7/24 yol yardım ve bakımlı araç filosu.',
    'campaign.close': 'Kapat',

    'about.title': 'Hakkımızda',
    'about.subtitle': 'Kurumsal yaklaşımımız ve güçlü operasyon altyapımızla uzun dönem kiralamada yanınızdayız.',
    'about.mission.title': 'Misyonumuz',
    'about.mission.body': 'Müşterilerimize en iyi araç kiralama deneyimini sunmak. Kaliteli hizmet anlayışımız ve geniş araç filomuzla, her müşterimizin ihtiyaçlarına uygun çözümler sunarak güvenli ve konforlu seyahat deneyimi yaşatmayı hedefliyoruz.',
    'about.vision.title': 'Vizyonumuz',
    'about.vision.body': "Türkiye'nin lider araç kiralama markası olmak. İnovatif hizmet anlayışımız, müşteri memnuniyeti odaklı yaklaşımımız ve sürekli gelişen teknolojik altyapımızla sektörde öncü konumumuzu güçlendirmeyi amaçlıyoruz.",

    'faq.title': 'Sıkça Sorulan Sorular',
    'terms.title': 'Kiralama Koşulları',
    'contact.title': 'İletişim',
    'quote.title': 'Hemen Teklif Alın',
    'quote.subtitle': '2 dakikada bilgilerinizi iletin, size en uygun teklif ile dönelim.',

    'features.card1.title': 'Güvenli Kiralama',
    'features.card1.desc': 'Güvenilir ödeme sistemlerimizle sorunsuz kiralama',
    'features.card2.title': 'Kaliteli Araç Filosu',
    'features.card2.desc': 'Bakımlı ve modern araç seçenekleri',
    'features.card3.title': 'Hızlı ve Kolay Araç Teslimatı',
    'features.card3.desc': 'Zamanında ve pratik teslimat süreçleri',
    'features.card4.title': 'Çevre Dostu Araç Seçenekleri',
    'features.card4.desc': 'Hibrit ve elektrikli araçlarla sürdürülebilir yolculuk',

    'values.title': 'Değerlerimiz',
    'values.transparency.title': 'Şeffaflık',
    'values.transparency.body': 'Sözleşme ve fiyatlandırmada net, öngörülebilir ve anlaşılır yaklaşım.',
    'values.reliability.title': 'Güvenilirlik',
    'values.reliability.body': 'Bakımlı filo, söz verilen zaman ve kapsamda hizmet teslimi.',
    'values.speed.title': 'Hız ve Esneklik',
    'values.speed.body': 'Operasyonel süreçlerde hızlı yanıt ve ihtiyaca göre esnek çözümler.',
    'values.customer.title': 'Müşteri Odaklılık',
    'values.customer.body': 'Uzun dönem ilişkilerde sürdürülebilir memnuniyet ve destek.',

    'vehicles.title': 'Araç Filomuz',
    'vehicles.filters': 'Filtreler',
    'vehicles.filter.class': 'Sınıf',
    'vehicles.filter.trans': 'Vites',
    'vehicles.filter.fuel': 'Yakıt',
    'vehicles.monthlyPrice': 'Aylık/Yıllık fiyat',
    'vehicles.priceOnRequest': 'Fiyat talep üzerine',
    'vehicles.quote': 'Teklif İste',
    'vehicles.results': 'sonuç',

    'testimonials.title': 'Müşterilerimiz Ne Diyor?',

    'footer.quick': 'Hızlı Linkler',
    'footer.location': 'Konumumuz',
    'footer.follow': 'Bizi Takip Edin',
    'footer.hours': 'Çalışma Saatleri',
    'footer.monfri': 'Pazartesi - Cuma',
    'footer.sat': 'Cumartesi',
    'footer.sun': 'Pazar',
    'footer.closed': 'Kapalı',
    'footer.cookies': 'Çerez Politikası',
    'footer.rights': 'Tüm Hakları Saklıdır.',
    'footer.about1': 'Kaliteli hizmet ve uygun fiyatlarla hayalinizdeki aracı kiralayın.',
    'footer.about2': 'Geniş araç filomuzla hizmetinizdeyiz.',

    // Corporate page
    'corporate.title': 'Kurumsal Araç Kiralama Şartları',
    'corporate.desc': 'Kurumsal uzun dönem araç kiralama: sözleşme, filo yönetimi, bakım ve raporlama ile uçtan uca çözüm.',
    'corporate.term1.title': 'Şirket Belgelerinin Sunulması',
    'corporate.term1.desc': 'Kurumsal araç kiralama sürecinde, şirketin yasal varlığını kanıtlayan belgelerin sunulması gereklidir. Araç kiralama firmaları genellikle şu belgeleri talep eder:',
    'corporate.term1.item.tax': 'Vergi Levhası',
    'corporate.term1.item.sign': 'İmza Sirküleri',
    'corporate.term1.item.gazette': 'Ticari Sicil Gazetesi',
    'corporate.term1.item.activity': 'Faaliyet Belgesi',
    'corporate.term1.item.findeks': 'Şirket Sahibinin Findeks Raporu',
    'corporate.term2.title': 'Araç Kullanacak Kişinin Bilgileri',
    'corporate.term2.desc': 'Kurumsal araç kiralamada, araçları kullanacak sürücünün bilgileri de büyük önem taşır. Kiralama sırasında, sürücünün kimlik belgesi, geçerli bir ehliyet ve bazen sabıka kaydı gibi belgeler istenir. Ayrıca sürücünün ehliyetinin en az 3 yıllık olması beklenir.',
    'corporate.term3.title': 'Ödeme ve Depozito Şartları',
    'corporate.term3.desc': 'Kurumsal araç kiralama şartlarında, ödeme ve depozito şartları şirketler tarafından dikkatle incelenmelidir. Araç kiralama firmaları, genellikle araç başına belirli bir depozito ücreti talep eder. Araç segmentine göre değişiklik gösterebilir.',

    // Individual page
    'individual.title': 'Bireysel Araç Kiralama Şartları',
    'individual.desc': 'Bireysel araç kiralama: ehliyet ve yaş şartı, ödeme ve depozito, kullanım koşulları.',
    'individual.t1': 'En az 3 yıllık ehliyet',
    'individual.t2': 'Findeks raporu',
    'individual.t3': 'İkametgah belgesi (E-devlet üzerinden alınmalı)',
    'individual.t4': 'Teminat ücreti',
  },
  en: {
    'nav.home': 'Home',
    'nav.cars': 'Our Cars',
    'nav.about': 'About Us',
    'nav.corporate': 'Corporate Leasing',
    'nav.callNow': 'Call Now',
    'nav.individual': 'Individual Leasing',

    'hero.title': 'Fast and Reliable Car Rental',
    'hero.subtitle': 'Rent at the best prices',
    'hero.cta': 'Get a Quote',

    'campaign.title': 'Campaign',
    'campaign.line1': 'This month: 5% web-only discount! Code:',
    'campaign.line2': 'Get a quote for special long-term rates.',
    'campaign.line3': '24/7 roadside assistance and a well-maintained fleet.',
    'campaign.close': 'Close',

    'about.title': 'About Us',
    'about.subtitle': 'With our corporate approach and strong operations, we are by your side in long-term leasing.',
    'about.mission.title': 'Our Mission',
    'about.mission.body': 'To deliver the best car rental experience. With our quality service approach and wide fleet, we offer solutions tailored to each need for a safe and comfortable journey.',
    'about.vision.title': 'Our Vision',
    'about.vision.body': "To become Turkey’s leading car rental brand. With an innovation-driven service mindset, customer focus and constantly evolving technology, we aim to strengthen our leadership.",

    'faq.title': 'Frequently Asked Questions',
    'terms.title': 'Rental Terms',
    'contact.title': 'Contact',
    'quote.title': 'Get a Quote',
    'quote.subtitle': 'Share your details in 2 minutes and we’ll get back with the best offer.',

    'features.card1.title': 'Secure Rental',
    'features.card1.desc': 'Seamless rental with secure payment systems',
    'features.card2.title': 'Quality Fleet',
    'features.card2.desc': 'Well-maintained and modern vehicle options',
    'features.card3.title': 'Fast & Easy Delivery',
    'features.card3.desc': 'On-time and convenient delivery',
    'features.card4.title': 'Eco-friendly Vehicle Options',
    'features.card4.desc': 'Sustainable journeys with hybrid and electric vehicles',

    'values.title': 'Our Values',
    'values.transparency.title': 'Transparency',
    'values.transparency.body': 'Clear and predictable contracts and pricing.',
    'values.reliability.title': 'Reliability',
    'values.reliability.body': 'Well-maintained fleet and on-time service delivery.',
    'values.speed.title': 'Speed & Flexibility',
    'values.speed.body': 'Fast responses and flexible solutions to your needs.',
    'values.customer.title': 'Customer Centricity',
    'values.customer.body': 'Sustainable satisfaction and support in long-term relations.',

    'vehicles.title': 'Our Fleet',
    'vehicles.filters': 'Filters',
    'vehicles.filter.class': 'Class',
    'vehicles.filter.trans': 'Transmission',
    'vehicles.filter.fuel': 'Fuel',
    'vehicles.monthlyPrice': 'Monthly/Yearly price',
    'vehicles.priceOnRequest': 'Price on request',
    'vehicles.quote': 'Request Quote',
    'vehicles.results': 'results',

    'testimonials.title': 'What Our Customers Say',

    'footer.quick': 'Quick Links',
    'footer.location': 'Our Location',
    'footer.follow': 'Follow Us',
    'footer.hours': 'Working Hours',
    'footer.monfri': 'Monday - Friday',
    'footer.sat': 'Saturday',
    'footer.sun': 'Sunday',
    'footer.closed': 'Closed',
    'footer.cookies': 'Cookie Policy',
    'footer.rights': 'All Rights Reserved.',
    'footer.about1': 'Rent your dream car with quality service at the right price.',
    'footer.about2': 'We are at your service with a wide fleet.',

    // Corporate page
    'corporate.title': 'Corporate Car Leasing Terms',
    'corporate.desc': 'Corporate long-term car leasing: contract, fleet management, maintenance and reporting.',
    'corporate.term1.title': 'Submission of Company Documents',
    'corporate.term1.desc': 'In corporate car leasing, documents proving the legal existence of the company are required. Rental companies typically request the following:',
    'corporate.term1.item.tax': 'Tax Certificate',
    'corporate.term1.item.sign': 'Signature Circular',
    'corporate.term1.item.gazette': 'Trade Registry Gazette',
    'corporate.term1.item.activity': 'Certificate of Activity',
    'corporate.term1.item.findeks': 'Owner’s Findeks Report',
    'corporate.term2.title': 'Driver Information',
    'corporate.term2.desc': 'Driver information is crucial. ID, valid driving licence and sometimes criminal record may be requested. A minimum of 3 years of licence is expected.',
    'corporate.term3.title': 'Payment and Deposit Terms',
    'corporate.term3.desc': 'Payment and deposit terms should be reviewed carefully. A per-vehicle deposit is commonly requested and varies by segment.',

    // Individual page
    'individual.title': 'Individual Car Leasing Terms',
    'individual.desc': 'Individual leasing: licence and age requirement, payment & deposit, usage terms.',
    'individual.t1': 'Minimum 3 years driving licence',
    'individual.t2': 'Findeks report',
    'individual.t3': 'Residence certificate (from e-Government)',
    'individual.t4': 'Deposit fee',
  },
};

const STORAGE_KEY = 'lang_v1';

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>('tr');

  useEffect(() => {
    const saved = (localStorage.getItem(STORAGE_KEY) as Lang | null) || 'tr';
    setLangState(saved);
    document.documentElement.lang = saved;
  }, []);

  const setLang = (l: Lang) => {
    setLangState(l);
    localStorage.setItem(STORAGE_KEY, l);
    document.documentElement.lang = l;
  };

  const t = (key: string) => (DICTS[lang][key] ?? DICTS['tr'][key] ?? key);

  const value = useMemo(() => ({ lang, setLang, t }), [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}; 